import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, X, Sparkles } from 'lucide-react';
import styles from '../../pages/JobDashboard/JobDetailNew.module.css';
import api from '../../api/api.js';
import showToast from "../../components/ShowToast";

const ProfessionSelector = ({ 
    selectedProfessions = [], 
    onUpdate, 
    isReadOnly = false, 
    suggestedProfessions = [],
    isProfessionLocked = false
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showFallback, setShowFallback] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);

    // Search Effect (Debounced)
    useEffect(() => {
        setShowFallback(false);
        setResults([]);

        if (searchTerm.length < 3) {
            setIsDropdownOpen(false);
            return;
        }

        const handler = setTimeout(async () => {
            setIsSearching(true);
            try {
                // Use the endpoint from JobCreationWizard
                const response = await api.get(`/api/Jobs/AISmartSearch?search=${encodeURIComponent(searchTerm)}`);
                const matchedProfessions = (response.data || []).map(p => ({ 
                    professionId: p.id || p.Id || p.professionId, 
                    name: p.name 
                }));

                if (matchedProfessions.length > 0) {
                    setResults(matchedProfessions);
                    setIsDropdownOpen(true);
                } else {
                    setShowFallback(true);
                    // Keep dropdown open to show "No results" or nothing? 
                    // JobCreationWizard shows fallback outside the dropdown. 
                    // Let's close dropdown and show fallback message.
                    setIsDropdownOpen(false);
                }
            } catch (error) {
                console.error("Failed to fetch smart search results:", error);
                setShowFallback(true);
            } finally {
                setIsSearching(false);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const handleSelect = (profession) => {
        if (isReadOnly || isProfessionLocked) return;

        // Ensure we don't add duplicates
        if (!selectedProfessions.some(p => p.professionId === profession.professionId)) {
            const newProfessions = [...selectedProfessions, profession];
            onUpdate(newProfessions);
        }
        
        setSearchTerm('');
        setResults([]);
        setIsDropdownOpen(false);
        setShowFallback(false);
    };

    const handleRemove = (professionId) => {
        if (isReadOnly || isProfessionLocked) return;
        onUpdate(selectedProfessions.filter(p => p.professionId !== professionId));
    };

    const handleReasoningSearch = async () => {
        if (isReadOnly || isProfessionLocked) return;

        setIsSearching(true);
        setShowFallback(false);
        try {
            const response = await api.post('/api/Jobs/AIReasoningSearch', { searchTerm });
            if (response.data && response.data.length > 0) {
                const aiSuggestions = response.data.map(p => ({ 
                    professionId: p.id || p.professionId || p.ProfessionId, 
                    name: p.name 
                }));
                
                // Add unique suggestions
                const newProfessions = [...selectedProfessions];
                aiSuggestions.forEach(suggestion => {
                    if (!newProfessions.some(p => p.professionId === suggestion.professionId)) {
                        newProfessions.push(suggestion);
                    }
                });
                onUpdate(newProfessions);
                setSearchTerm('');
            } else {
                showToast('info', "Our advanced search couldn't find a specific match. Please try rephrasing your request.");
            }
        } catch (error) {
            showToast('error', `Advanced AI search failed: ${error.response?.data?.message || 'Please try again.'}`);
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <div className={styles.jobDetailNew_card}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_cardHeaderGradient}`}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Search className={styles.jobDetailNew_iconMd} />
                  <span>Who are you looking for?</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY4}`}>
                
                {/* SEARCH INPUT */}
                <div className={styles.jobDetailNew_inputWithIconContainer} ref={dropdownRef}>
                  <Search className={styles.jobDetailNew_inputIcon} />
                  <input 
                    type="text" 
                    placeholder={isProfessionLocked ? "Profession selection locked (job has leads)" : "Search for professionals (e.g., Electrician)"} 
                    className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={isReadOnly || isProfessionLocked}
                  />
                  
                  {/* DROPDOWN RESULTS */}
                  {isDropdownOpen && results.length > 0 && !isReadOnly && !isProfessionLocked && (
                    <ul className={styles.jobDetailNew_dropdownResults}>
                        <p style={{padding: '0.5rem 1rem', fontSize: '0.8rem', color: '#6b7280', fontWeight: 'bold'}}>Did you mean...?</p>
                        {results.map((item) => (
                        <li
                          key={item.professionId}
                          className={styles.jobDetailNew_resultItem}
                          onClick={() => handleSelect(item)}
                        >
                          <Plus size={14} style={{marginRight: '8px'}}/> {item.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* AI FALLBACK */}
                {showFallback && !isSearching && !isReadOnly && !isProfessionLocked && (
                    <div style={{textAlign: 'center', marginTop: '1rem', padding: '1rem', backgroundColor: '#eff6ff', borderRadius: '8px'}}>
                        <p className={`${styles.jobDetailNew_textGray700} ${styles.jobDetailNew_mb2}`}>**Can't find the right profession?**</p>
                        <p className={`${styles.jobDetailNew_textGray600} ${styles.jobDetailNew_mb4}`} style={{fontSize: '0.9rem'}}>Let our advanced AI analyze your request to find the best matches for you.</p>
                        <button onClick={handleReasoningSearch} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`} style={{marginTop: '0.75rem'}}>
                            <Sparkles size={16} style={{marginRight: '8px'}}/> Use Advanced AI Search
                        </button>
                    </div>
                )}

                {/* SELECTED BADGES */}
                <div>
                  <label className={styles.jobDetailNew_label}>Selected Professions:</label>
                  <div className={styles.jobDetailNew_flexWrap}>
                    {selectedProfessions.map((profession) => (
                      <span key={profession.professionId} className={styles.jobDetailNew_badge}>
                        {profession.name}
                        {!isReadOnly && !isProfessionLocked && (
                          <button
                            type="button"
                            className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost} ${styles.jobDetailNew_buttonSm}`}
                            onClick={() => handleRemove(profession.professionId)}
                          >
                            <X className={styles.jobDetailNew_buttonIcon} />
                          </button>
                        )}
                      </span>
                    ))}
                    {selectedProfessions.length === 0 && <span style={{color: '#9ca3af', fontStyle: 'italic', fontSize: '0.9rem'}}>None selected</span>}
                  </div>
                </div>

                {/* SUGGESTIONS (Optional) */}
                {suggestedProfessions.length > 0 && !isProfessionLocked && (
                    <div>
                    <label className={styles.jobDetailNew_label}>Suggested Professions:</label>
                    <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols3}`}>
                        {suggestedProfessions
                        .filter((prof) => !selectedProfessions.some(sp => sp.professionId === prof.professionId))
                        .slice(0, 9) // Limit to 9 suggestions to not clutter
                        .map((profession) => (
                            <button
                            type="button"
                            key={profession.professionId}
                            className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline} ${styles.jobDetailNew_buttonOutlineSm} ${styles.jobDetailNew_buttonJustifyStart}`}
                            onClick={() => handleSelect(profession)}
                            disabled={isReadOnly || isProfessionLocked}
                            >
                            <Plus className={`${styles.jobDetailNew_buttonIcon} ${styles.jobDetailNew_buttonIconMr2}`} />
                            {profession.name}
                            </button>
                        ))}
                    </div>
                    </div>
                )}
              </div>
            </div>
    );
};

export default ProfessionSelector;
